import React, { useMemo } from 'react'

import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { mergeDeepRight, mergeRight } from 'ramda'

import { ReportDateHistogramType } from '@driftt/api-types'

import { EmailCampaignMetricType } from 'api/email/types'
import {
  getHumanizedShortDuration,
  getStartOfDayUnix,
  TypeFormats,
} from 'utils/format'

import { lineGraphTooltipFormatter } from '../highcharts/formatters'
import { PointInterval } from '../highcharts/utils'

import OPTIONS from './options'

export const LINE_CHART_NUMERIC_VALUE_METRICS = Object.freeze([
  'newConversations',
  'meetingsBookedByDate',
  'callsMadeByDate',
  'conversations',
  'leadsCaptured',
  'meetingsBooked',
  'serviceDeflection',
  ...Object.values(EmailCampaignMetricType),
])

const yAxisTimeFormatter = function () {
  return getHumanizedShortDuration(this.value)
}

const yAxisNumberFormatter = function () {
  return TypeFormats.RAW_HUMANIZED(this.value)
}

const yAxisPercentFormatter = function () {
  return TypeFormats.PERCENT(this.value)
}

const LineGraph = ({
  currentSeries,
  currentSeriesLabel,
  prevSeries,
  prevSeriesLabel,
  queryConfig,
  invertDelta = false,
  interval = ReportDateHistogramType.DAY,
  options = {},
  onClick = undefined,
}: {
  onClick?: (date: string) => void
  [key: string]: any
}) => {
  const isServiceDeflection = queryConfig.metric === 'serviceDeflection'

  const chartOptions: Highcharts.Options = useMemo(() => {
    const onSeriesClick = onClick
      ? {
          cursor: 'pointer',
          events: {
            click: (event) => {
              onClick(event.point.name)
            },
          },
        }
      : undefined
    return mergeDeepRight(
      OPTIONS,
      mergeDeepRight(
        {
          tooltip: {
            formatter: lineGraphTooltipFormatter(queryConfig.metric, {
              invertDelta,
            }),
          },
          series: [
            mergeRight(OPTIONS.series![0], {
              name: currentSeriesLabel,
              data: currentSeries,
              pointStart: getStartOfDayUnix(currentSeries[0][0]),
              pointInterval: PointInterval[interval],
              ...onSeriesClick,
            }),
            prevSeries &&
              mergeRight(OPTIONS.series![1], {
                name: prevSeriesLabel,
                data: prevSeries,
                pointStart: getStartOfDayUnix(currentSeries[0][0]),
                pointInterval: PointInterval[interval],
                ...onSeriesClick,
              }),
          ].filter(Boolean),
          yAxis: {
            labels: {
              formatter: LINE_CHART_NUMERIC_VALUE_METRICS.includes(
                queryConfig.metric
              )
                ? isServiceDeflection
                  ? yAxisPercentFormatter
                  : yAxisNumberFormatter
                : yAxisTimeFormatter,
            },
          },
        } as any,
        options
      )
    )
  }, [
    onClick,
    queryConfig.metric,
    invertDelta,
    currentSeriesLabel,
    currentSeries,
    interval,
    prevSeriesLabel,
    prevSeries,
    options,
    isServiceDeflection,
  ])

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />
}

export default LineGraph
